import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { Lock, CreditCard, Loader2, CheckCircle, XCircle, Home, Bitcoin, Sun, Moon, WalletCards, Globe2, ChevronDown } from 'lucide-react';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const LOGO_URL = "/logo-alphabit.png";

export default function PublicPaymentPage() {
  const { linkId } = useParams();
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const { currentLanguage, changeLanguage, languages, t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [processingGatewayId, setProcessingGatewayId] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [paidAmount, setPaidAmount] = useState(null); // Store the actual paid amount
  const [paidCurrency, setPaidCurrency] = useState(null); // Store the currency
  const [prefillTriggered, setPrefillTriggered] = useState(false);

  useEffect(() => {
    fetchPaymentLink();
    
    // Check if returning from payment
    const urlParams = new URLSearchParams(window.location.search);
    const orderId = urlParams.get('token');
    if (orderId) {
      capturePayment(orderId);
    }
  }, [linkId]);

  useEffect(() => {
    if (!data || prefillTriggered) return;
    const urlParams = new URLSearchParams(window.location.search);
    const amountParam = urlParams.get('amount');
    const orderIdParam = urlParams.get('order_id') || urlParams.get('orderId') || urlParams.get('orderID');
    const hasGatewayStatus = urlParams.get('nexi_status') || urlParams.get('payplug_status') || urlParams.get('crypto_status') || urlParams.get('gateway') || urlParams.get('status');
    if (!amountParam || !orderIdParam || hasGatewayStatus) return;

    if (!data?.merchant?.prefill_enabled) {
      if (!data?.payment_link?.amount) {
        setCustomAmount(amountParam);
      }
      return;
    }

    const prefillKey = `prefill_${linkId}_${amountParam}_${orderIdParam}`;
    if (sessionStorage.getItem(prefillKey)) return;
    sessionStorage.setItem(prefillKey, '1');

    const runPrefill = async () => {
      setPrefillTriggered(true);
      try {
        const response = await axios.post(`${API_URL}/api/public/payment-link/${linkId}/prefill`, {
          amount: amountParam,
          order_id: orderIdParam
        });
        const redirectUrl = response.data?.relative_url || response.data?.redirect_url;
        if (redirectUrl) {
          window.location.href = redirectUrl;
          return;
        }
      } catch (error) {
        console.error('Prefill error:', error);
        toast.error('Errore nella precompilazione del pagamento');
        if (!data?.payment_link?.amount) {
          setCustomAmount(amountParam);
        }
      }
    };

    runPrefill();
  }, [data, linkId, prefillTriggered]);

  const fetchPaymentLink = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/public/payment-link/${linkId}`);
      setData(response.data);
      // Save return URL for the result page "Torna al Negozio" button
      // Priority: success_redirect > fail_redirect > metadata.site_url
      const pl = response.data.payment_link;
      const returnUrl = pl?.success_redirect || pl?.fail_redirect || pl?.metadata?.site_url;
      if (returnUrl) {
        sessionStorage.setItem('alphabit_return_url', returnUrl);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Link di pagamento non trovato');
    } finally {
      setLoading(false);
    }
  };

  const capturePayment = async (orderId) => {
    setProcessing(true);
    try {
      const response = await axios.post(`${API_URL}/api/payments/capture-order/${orderId}`);
      if (response.data.status === 'success') {
        setPaymentStatus('success');
        // Save the paid amount from the response
        if (response.data.amount) {
          setPaidAmount(response.data.amount);
          setPaidCurrency(response.data.currency || 'EUR');
        }
        toast.success('Pagamento completato!');
        // Refresh payment link data to show updated status
        fetchPaymentLink();
      } else {
        setPaymentStatus('failed');
        toast.error('Pagamento fallito');
      }
    } catch (error) {
      console.error('Capture error:', error);
      
      // Check if the payment link is already paid (by checking status)
      try {
        const linkResponse = await axios.get(`${API_URL}/api/public/payment-link/${linkId}`);
        if (linkResponse.data.payment_link.status === 'paid') {
          // Payment was already completed successfully
          setPaymentStatus('success');
          toast.success('Pagamento completato!');
          setData(linkResponse.data);
          return;
        }
      } catch (e) {
        // Ignore errors from status check
      }
      
      setPaymentStatus('failed');
      toast.error('Errore nella conferma del pagamento');
    } finally {
      setProcessing(false);
    }
  };

  const handlePayment = async (
    selectedGateway = null,
    selectedGatewayId = null,
    selectedProviderId = null,
    selectedPaymentMethod = null
  ) => {
    const amount = data.payment_link.amount || parseFloat(customAmount);
    if (!amount || amount <= 0) {
      toast.error('Inserisci un importo valido');
      return;
    }

    setProcessing(true);
    const gateway = selectedGateway || data.payment_link.gateway || data.merchant?.gateway || 'paypal';
    const gatewayId = selectedGatewayId ?? data.payment_link.gateway_id ?? null;
    const processingKey = `${gatewayId ?? gateway}:${selectedPaymentMethod ?? selectedProviderId ?? ''}`;
    setProcessingGatewayId(processingKey);
    try {
      // Use selected gateway or default
      
      // Select the correct API endpoint based on gateway
      let apiEndpoint = `${API_URL}/api/payments/create-order`;
      if (gateway === 'viva_wallet') {
        apiEndpoint = `${API_URL}/api/payments/viva/create-order`;
      } else if (gateway === 'roxpay') {
        apiEndpoint = `${API_URL}/api/payments/roxpay/create-order`;
      } else if (gateway === 'crypto') {
        apiEndpoint = `${API_URL}/api/payments/crypto/create-order`;
      } else if (gateway === 'nexi') {
        apiEndpoint = `${API_URL}/api/payments/nexi/create-order`;
      } else if (gateway === 'payplug') {
        apiEndpoint = `${API_URL}/api/payments/payplug/create-order`;
      } else if (gateway === 'sumup') {
        apiEndpoint = `${API_URL}/api/payments/sumup/create-order`;
      } else if (gateway === 'mollie') {
        apiEndpoint = `${API_URL}/api/payments/mollie/create-order`;
      } else if (gateway === 'silkpay') {
        apiEndpoint = `${API_URL}/api/payments/silkpay/create-order`;
      } else if (gateway === 'usda_pay') {
        apiEndpoint = `${API_URL}/api/payments/usda/create-order`;
      }
      
      // Build params - include gateway_id if provided for multi-gateway support
      const params = {
        payment_link_id: linkId,
        custom_amount: data.payment_link.amount ? null : amount
      };
      
      // CRITICAL: Pass gateway_id to backend for multi-gateway
       if (gatewayId != null) {
        params.selected_gateway_id = gatewayId;
      }
       // RoxPay can expose several catalog methods under one configured gateway.
       // Keep this parameter exclusive to RoxPay so all other gateways retain
       // their existing request contract.
       if (gateway === 'roxpay' && selectedProviderId != null) {
         params.selected_provider_id = selectedProviderId;
       }
       if (gateway === 'roxpay' && selectedPaymentMethod != null) {
         params.selected_payment_method = selectedPaymentMethod;
       }
      
      const response = await axios.post(apiEndpoint, null, { params });

      // Handle different gateway responses
      if (response.data.approval_url) {
        // PayPal
        window.location.href = response.data.approval_url;
      } else if (response.data.hosted_checkout_url) {
        // SumUp Hosted Checkout
        window.location.href = response.data.hosted_checkout_url;
      } else if (response.data.checkout_url) {
        // Viva Wallet / Rox Pay - Save order info to localStorage for confirmation after redirect
        if (gateway === 'viva_wallet' && response.data.order_code) {
          localStorage.setItem('viva_pending_order', JSON.stringify({
            order_code: response.data.order_code,
            transaction_id: response.data.transaction_id,
            payment_link_id: linkId,
            timestamp: Date.now()
          }));
        }
        window.location.href = response.data.checkout_url;
      } else if (response.data.payment_url) {
        // Rox Pay alternative
        window.location.href = response.data.payment_url;
      } else if (gateway === 'usda_pay' && response.data.qr_code && response.data.order_id) {
        // USDA Pay - navigate to inline checkout
        navigate(`/pay/usda/${response.data.order_id}`);
      } else {
        toast.error('Errore nella creazione del pagamento');
        setProcessing(false);
        setProcessingGatewayId(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel pagamento');
      setProcessing(false);
      setProcessingGatewayId(null);
    }
  };

  const formatCurrency = (amount, currency) => {
    const intlLocales = {
      it: 'it-IT',
      en: 'en-GB',
      fr: 'fr-FR',
      de: 'de-DE',
      es: 'es-ES',
      ar: 'ar-SA',
      ru: 'ru-RU'
    };
    return new Intl.NumberFormat(intlLocales[currentLanguage] || 'it-IT', {
      style: 'currency',
      currency: currency || 'EUR'
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#0B0F2A]' : 'bg-slate-50'}`}>
        <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${isDark ? 'border-[#9D4DFF]' : 'border-primary'}`}></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#0B0F2A]' : 'bg-slate-50'}`}>
        <Card className={`max-w-md w-full mx-4 ${isDark ? 'bg-[#0F0F2F] border-purple-500/30' : ''}`}>
          <CardContent className="p-8 text-center">
            <XCircle className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-red-400' : 'text-red-500'}`} />
            <h1 className={`text-xl font-heading font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {t('payment.invalidLink')}
            </h1>
            <p className={isDark ? 'text-gray-400' : 'text-slate-600'}>
              {t('payment.invalidLinkMessage')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { payment_link, merchant, available_gateways } = data;
  const linkTitle = payment_link.title || payment_link.description || '';
  const branding = merchant?.branding || {};
  const primaryColor = branding.primary_color || '#7c3aed';
  const buttonColor = branding.button_color || '#7c3aed';
  
  // Get gateway name for display
  const gateway = payment_link.gateway || merchant?.gateway || 'paypal';
  const gatewayNames = {
    paypal: 'PayPal',
    viva_wallet: 'Viva Wallet',
    roxpay: 'Rox Pay',
    stripe: 'Stripe',
    crypto: 'Crypto',
    nexi: 'NEXI XPay',
    payplug: 'PayPlug',
    sumup: 'SumUp',
    mollie: 'Mollie',
    silkpay: 'SilkPay',
    bank_transfer: 'Bonifico Bancario'
  };
  const gatewayDisplayName = gatewayNames[gateway] || gateway;
  
  // Gateway logos - real logos
  const gatewayLogos = {
    paypal: '/payment-brands/paypal-card-logo.png',
    viva_wallet: 'https://www.vivawallet.com/assets/images/logo.svg',
    roxpay: null, // Will use fallback
    stripe: 'https://upload.wikimedia.org/wikipedia/commons/b/ba/Stripe_Logo%2C_revised_2016.svg',
    crypto: 'https://upload.wikimedia.org/wikipedia/commons/4/46/Bitcoin.svg',
    nexi: 'https://www.nexi.it/content/dam/nexi/loghi/logo-nexi.svg',
    payplug: null, // Will use fallback
    sumup: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxMjAgNDAiPjxyZWN0IHdpZHRoPSIxMjAiIGhlaWdodD0iNDAiIGZpbGw9IiMxQTFGNzEiIHJ4PSI0Ii8+PHRleHQgeD0iNjAiIHk9IjI2IiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTYiIGZvbnQtd2VpZ2h0PSJib2xkIiBmaWxsPSJ3aGl0ZSIgdGV4dC1hbmNob3I9Im1pZGRsZSI+U3VtVXA8L3RleHQ+PC9zdmc+',
    mollie: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxMjAgNDAiPjxyZWN0IHdpZHRoPSIxMjAiIGhlaWdodD0iNDAiIGZpbGw9IiMwMDBGMjIiIHJ4PSI0Ii8+PHRleHQgeD0iNjAiIHk9IjI2IiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTYiIGZvbnQtd2VpZ2h0PSJib2xkIiBmaWxsPSJ3aGl0ZSIgdGV4dC1hbmNob3I9Im1pZGRsZSI+bW9sbGllPC90ZXh0Pjwvc3ZnPg==',
    bank_transfer: null // Will use fallback
  };
  
  // A RoxPay gateway is a catalog of payment methods, so expose each method
  // as its own choice while keeping the configured gateway id intact.
  const paymentChoices = (available_gateways || []).flatMap((gw) => {
    if (
      gw.name === 'roxpay'
      && gw.payment_methods_configured
      && (!Array.isArray(gw.payment_methods) || gw.payment_methods.length === 0)
    ) {
      return [];
    }
    if (gw.name !== 'roxpay' || !Array.isArray(gw.payment_methods) || gw.payment_methods.length === 0) {
      return [{
        key: `${gw.id}-${gw.name}`,
        gatewayName: gw.name,
        gatewayId: gw.id,
        providerId: null,
        methodKey: null,
        label: gw.name === 'usda_pay' ? 'USDA Pay' : (gatewayNames[gw.name] || gw.name),
        kind: null,
        description: null
      }];
    }

    return gw.payment_methods.map((method, index) => ({
      key: `${gw.id}-${method.method_key ?? method.provider_id ?? index}`,
      gatewayName: gw.name,
      gatewayId: gw.id,
      providerId: method.provider_id,
      methodKey: method.method_key || null,
      label: method.kind === 'card'
        ? 'Carta di Credito/Debito'
        : (method.label || 'Metodo di pagamento'),
      kind: method.kind || 'generic',
      description: method.description || null
    }));
  });
  const paymentChoiceOrder = {
    card: 1,
    apple_pay: 2,
    google_pay: 3,
    wallet: 4,
    bank: 5,
    klarna: 6
  };
  const orderedPaymentChoices = [...paymentChoices].sort((a, b) => {
    const aOrder = a.gatewayName === 'usda_pay' ? 0 : (a.gatewayName === 'roxpay' ? (paymentChoiceOrder[a.kind] ?? 10) : 20);
    const bOrder = b.gatewayName === 'usda_pay' ? 0 : (b.gatewayName === 'roxpay' ? (paymentChoiceOrder[b.kind] ?? 10) : 20);
    return aOrder - bOrder;
  });
  const singleChoice = paymentChoices.length === 1 ? paymentChoices[0] : null;
  const showPaymentChoices = paymentChoices.length > 1
    || (
      singleChoice?.gatewayName === 'roxpay'
      && (singleChoice?.providerId != null || singleChoice?.methodKey != null)
    );

  // Payment completed screen
  if (paymentStatus === 'success') {
    // Use paidAmount if available (from capture response), otherwise fallback to payment_link amount or customAmount
    const displayAmount = paidAmount || payment_link.amount || parseFloat(customAmount) || 0;
    const displayCurrency = paidCurrency || payment_link.currency || 'EUR';
    
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#0B0F2A]' : 'bg-slate-50'}`}>
        <Card className={`max-w-md w-full mx-4 ${isDark ? 'bg-[#0F0F2F] border-purple-500/30' : ''}`}>
          <CardContent className="p-8 text-center">
            <CheckCircle className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-emerald-400' : 'text-emerald-500'}`} />
            <h1 className={`text-2xl font-heading font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {t('payment.paymentCompleted')}
            </h1>
            <p className={`mb-6 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
              {t('payment.thankYou')}
            </p>
            <p className="text-3xl font-bold mb-6" style={{ color: primaryColor }}>
              {formatCurrency(displayAmount, displayCurrency)}
            </p>
            <Button
              onClick={() => navigate('/')}
              className="w-full"
              style={{ backgroundColor: buttonColor }}
            >
              <Home className="w-4 h-4 mr-2" />
              {t('auth.backToHome')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Payment failed screen
  if (paymentStatus === 'failed') {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#0B0F2A]' : 'bg-slate-50'}`}>
        <Card className={`max-w-md w-full mx-4 ${isDark ? 'bg-[#0F0F2F] border-purple-500/30' : ''}`}>
          <CardContent className="p-8 text-center">
            <XCircle className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-red-400' : 'text-red-500'}`} />
            <h1 className={`text-2xl font-heading font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {t('payment.paymentFailed')}
            </h1>
            <p className={`mb-6 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
              {t('payment.errorOccurred')}
            </p>
            <Button 
              onClick={() => setPaymentStatus(null)} 
              style={{ backgroundColor: buttonColor }}
              className="text-white"
            >
              {t('payment.retry')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Link expired or paid
  if (payment_link.status === 'expired') {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#0B0F2A]' : 'bg-slate-50'}`}>
        <Card className={`max-w-md w-full mx-4 ${isDark ? 'bg-[#0F0F2F] border-purple-500/30' : ''}`}>
          <CardContent className="p-8 text-center">
            <XCircle className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-amber-400' : 'text-amber-500'}`} />
            <h1 className={`text-xl font-heading font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {t('payment.linkExpired')}
            </h1>
            <p className={`mb-6 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
              {t('payment.linkExpiredMessage')}
            </p>
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className={`w-full ${isDark ? 'border-purple-500/30 text-white hover:bg-purple-500/10' : ''}`}
            >
              <Home className="w-4 h-4 mr-2" />
              {t('auth.backToHome')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (payment_link.status === 'paid') {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#0B0F2A]' : 'bg-slate-50'}`}>
        <Card className={`max-w-md w-full mx-4 ${isDark ? 'bg-[#0F0F2F] border-purple-500/30' : ''}`}>
          <CardContent className="p-8 text-center">
            <CheckCircle className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-emerald-400' : 'text-emerald-500'}`} />
            <h1 className={`text-xl font-heading font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {t('payment.alreadyPaid')}
            </h1>
            <p className={`mb-6 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
              {t('payment.alreadyPaidMessage')}
            </p>
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className={`w-full ${isDark ? 'border-purple-500/30 text-white hover:bg-purple-500/10' : ''}`}
            >
              <Home className="w-4 h-4 mr-2" />
              {t('auth.backToHome')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div
      className={`alphabit-checkout min-h-[100dvh] px-5 pb-7 pt-4 sm:px-6 sm:py-8 ${
        isDark ? 'bg-[#080824] text-white' : 'bg-slate-50 text-slate-900'
      }`}
      data-testid="public-payment-page"
    >
      <div className="mx-auto w-full max-w-[616px]">
        <header className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
          <img
            src={LOGO_URL}
            alt="AlphaBitPay"
            className="h-11 w-11 object-contain"
            onError={(e) => { e.target.src = LOGO_URL; }}
          />
          <div className="leading-none">
            <h1 className={`text-[22px] font-extrabold tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
              AlphaBit<span className="text-violet-400">Pay</span>
            </h1>
            <p className={`mt-1.5 text-[8px] font-medium tracking-[0.16em] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>FAST <span className="mx-1 text-violet-400">•</span> SECURE <span className="mx-1 text-violet-400">•</span> GLOBAL</p>
          </div>
          </div>
          <div className="flex items-center gap-2">
            <label
              className={`relative flex h-10 items-center gap-1.5 rounded-full px-2 text-sm transition-colors ${
                isDark ? 'text-slate-300 hover:bg-violet-500/10' : 'text-slate-600 hover:bg-violet-100'
              }`}
              aria-label={t('common.selectLanguage')}
            >
              <Globe2 className="h-5 w-5" />
              <select
                value={currentLanguage}
                onChange={(event) => changeLanguage(event.target.value)}
                className="h-10 cursor-pointer appearance-none bg-transparent px-0 pr-4 text-sm font-medium uppercase outline-none"
                aria-label={t('common.selectLanguage')}
                data-testid="checkout-language-selector"
              >
                {languages.map((language) => (
                  <option key={language.code} value={language.code}>
                    {language.code.toUpperCase()}
                  </option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-1 h-4 w-4" />
            </label>
            <button
              onClick={toggleTheme}
              className={`flex h-10 w-10 items-center justify-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-violet-400/60 ${
                isDark
                  ? 'bg-violet-500/15 text-amber-300 hover:bg-violet-500/25'
                  : 'bg-violet-100 text-violet-700 hover:bg-violet-200'
              }`}
              data-testid="theme-toggle"
              aria-label={isDark ? 'Passa alla modalità chiara' : 'Passa alla modalità scura'}
            >
              {isDark ? <Sun className="h-6 w-6" /> : <Moon className="h-6 w-6" />}
            </button>
          </div>
        </header>

        <Card className={`alphabit-panel overflow-hidden rounded-2xl shadow-none ${
          isDark
            ? 'border border-violet-500/30 bg-[#100d31]/95'
            : 'border border-slate-200 bg-white shadow-lg shadow-slate-200/60'
        }`}>
          <CardContent className="p-5">
            {linkTitle && (
              <div className={`mb-5 rounded-xl border px-5 py-4 text-[16px] ${
                isDark
                  ? 'border-violet-500/20 bg-[#1a1442] text-slate-200'
                  : 'border-violet-200 bg-violet-50 text-slate-700'
              }`}>
                {linkTitle}
              </div>
            )}

            <div className="mb-4 text-center">
              <p className={`text-[15px] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                {t('payment.amountToPay')}
              </p>
              {payment_link.amount ? (
                <p className="mt-1 text-[43px] font-bold leading-none tracking-tight text-fuchsia-400">
                  {formatCurrency(payment_link.amount, payment_link.currency)}
                </p>
              ) : (
                <div className="mt-2 space-y-2">
                  <Input
                    type="number"
                    step="0.01"
                    min="1"
                    placeholder={t('payment.enterAmount')}
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    className={`h-14 text-3xl font-bold ${
                      isDark
                        ? 'border-violet-500/35 bg-transparent text-white placeholder:text-slate-600'
                        : 'border-violet-300 bg-white text-slate-900 placeholder:text-slate-400'
                    }`}
                    data-testid="custom-amount-input"
                  />
                   <p className="text-xs text-slate-500">Valuta: {payment_link.currency}</p>
                </div>
              )}
            </div>

          {/* Pay button - Single or Multi gateway */}
          {paymentChoices.length === 0 ? (
            <div
              role="status"
              data-testid="payment-methods-unavailable"
              className={`rounded-xl border p-4 text-center ${
                isDark
                  ? 'border-amber-500/30 bg-amber-500/10 text-amber-200'
                  : 'border-amber-200 bg-amber-50 text-amber-800'
              }`}
            >
              <XCircle className="mx-auto h-6 w-6" />
              <p className="mt-2 text-sm font-medium">Metodo di pagamento temporaneamente non disponibile</p>
              <p className="mt-1 text-xs">Riprova tra poco oppure contatta l’esercente.</p>
            </div>
          ) : showPaymentChoices ? (
            // Show explicit method cards for multi-choice checkout and RoxPay.
             <div>
                <p className={`mb-3 text-center text-[15px] ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                  {t('payment.chooseMethod')}
                </p>
               <div className={`grid gap-2.5 ${orderedPaymentChoices.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                {orderedPaymentChoices.map((choice, choiceIndex) => (
                  <Button
                    key={choice.key}
                    onClick={() => handlePayment(
                      choice.gatewayName,
                      choice.gatewayId,
                      choice.providerId,
                      choice.methodKey
                    )}
                     disabled={processing || (!payment_link.amount && !customAmount)}
                    variant="outline"
                    aria-label={`Paga con ${choice.label}`}
                     aria-pressed={processingGatewayId === `${choice.gatewayId ?? choice.gatewayName}:${choice.methodKey ?? choice.providerId ?? ''}`}
                       className={`group relative flex h-[82px] items-center justify-center rounded-xl border p-2 text-center transition-all duration-200 hover:border-violet-400/70 hover:bg-violet-500/[.07] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-300/80 sm:h-[92px] ${
                       processingGatewayId === `${choice.gatewayId ?? choice.gatewayName}:${choice.methodKey ?? choice.providerId ?? ''}`
                          ? 'border-violet-300 bg-violet-500/15 shadow-[0_0_0_1px_rgba(168,85,247,.35),0_12px_28px_rgba(139,92,246,.16)]'
                           : choice.kind === 'card'
                             ? isDark
                               ? 'border-fuchsia-500 bg-fuchsia-500/[.04] shadow-[0_0_0_1px_rgba(217,70,239,.35)]'
                               : 'border-fuchsia-400 bg-fuchsia-50 shadow-[0_0_0_1px_rgba(217,70,239,.25)]'
                             : isDark
                               ? 'border-violet-500/25 bg-[#0d0c2c]/70'
                               : 'border-violet-200 bg-white'
                     }`}
                    data-testid={`pay-button-${choice.gatewayName}-${choice.methodKey ?? choice.providerId ?? choice.gatewayId}`}
                  >
                    {choice.kind === 'card' && !processing && (
                      <span className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-gradient-to-br from-fuchsia-500 to-violet-500 shadow-[0_0_12px_rgba(217,70,239,.55)]">
                        <CheckCircle className="h-4 w-4 text-white" strokeWidth={2.5} />
                      </span>
                    )}
                    {processing && processingGatewayId === `${choice.gatewayId ?? choice.gatewayName}:${choice.methodKey ?? choice.providerId ?? ''}` ? (
                      <Loader2 className="w-6 h-6 animate-spin" />
                    ) : choice.kind === 'klarna' ? (
                      <div className="flex items-center justify-center">
                        <img
                          src="/payment-brands/klarna-logo-cropped.png"
                          alt="Klarna"
                           className="h-10 w-24 rounded-md object-contain"
                        />
                      </div>
                    ) : choice.kind === 'bank' ? (
                      <img
                        src={isDark
                          ? '/payment-brands/instant-bank-transfer-transparent.png'
                          : '/payment-brands/instant-bank-transfer.png'}
                        alt="Bonifico istantaneo, oltre 170 banche"
                        className="h-[58px] w-[142px] object-contain"
                      />
                    ) : choice.kind === 'wallet' ? (
                      <div className="flex flex-col items-center gap-1.5">
                         <span className="relative">
                           <WalletCards className="h-8 w-8 text-white" strokeWidth={2} />
                           <span className="absolute -bottom-1 -right-2 flex h-4 w-4 items-center justify-center rounded-full bg-cyan-400 text-[9px] font-black text-[#080824]">R</span>
                         </span>
                        <span className="text-[12px] font-medium text-slate-100 sm:text-[13px]">
                          Wallet <span className="font-semibold text-cyan-400">RoxPay</span>
                        </span>
                      </div>
                    ) : choice.kind === 'google_pay' ? (
                      <div className="flex items-center justify-center">
                        <span className="inline-flex h-10 w-[86px] items-center justify-center gap-1.5 rounded-xl bg-white" aria-hidden="true">
                          <span
                            className="relative block h-6 w-6 rounded-full"
                            style={{ background: 'conic-gradient(from -35deg, #4285f4 0 25%, #34a853 25% 43%, #fbbc05 43% 64%, #ea4335 64% 82%, #4285f4 82% 100%)' }}
                          >
                            <span className="absolute inset-[5px] rounded-full bg-white" />
                            <span className="absolute right-0 top-[10px] h-[5px] w-[11px] bg-[#4285f4]" />
                          </span>
                          <span className="text-[24px] font-normal leading-none tracking-tight text-[#4b5563]">Pay</span>
                        </span>
                      </div>
                    ) : choice.kind === 'apple_pay' ? (
                      <div className="flex items-center justify-center">
                         <span className="inline-flex h-10 w-[86px] items-center justify-center gap-1 rounded-xl bg-white text-black" aria-hidden="true">
                           <svg viewBox="170 140 85 95" className="h-7 w-7 fill-current" role="img" aria-label="Apple">
                             <path d="m216.94 160.006c2.883-3.496 4.829-8.354 4.3-13.196-4.166.171-9.204 2.784-12.171 6.267-2.687 3.091-5.012 8.041-4.37 12.787 4.633.358 9.358-2.363 12.241-5.858zm11.97 30.724c-.092-10.471 8.542-15.508 8.942-15.758-4.871-7.083-12.425-8.066-15.116-8.175-6.438-.637-12.575 3.792-15.829 3.792-3.266 0-8.291-3.692-13.641-3.579-7.017.099-13.487 4.079-17.099 10.358-7.271 12.678-1.846 31.403 5.253 41.657 3.476 5.02 7.608 10.661 13.054 10.457 5.242-.204 7.208-3.391 13.542-3.391 6.32-.008 8.108 3.383 13.628 3.287 5.633-.121 9.208-5.125 12.662-10.17 3.971-5.813 5.617-11.458 5.7-11.741-.113-.076-10.971-4.217-11.096-16.737z" />
                           </svg>
                           <span className="text-[25px] font-normal leading-none tracking-tight">Pay</span>
                        </span>
                      </div>
                    ) : choice.kind === 'card' ? (
                       <div className="flex flex-col items-center gap-1">
                         <span className="relative flex h-[35px] w-[56px] overflow-hidden rounded bg-gradient-to-br from-sky-400 via-blue-600 to-indigo-800 p-1 text-left shadow-md">
                           <span className="absolute right-2 top-1.5 text-[11px] font-black italic text-white sm:text-[12px]">VISA</span>
                           <span className="absolute left-1.5 top-2 h-2 w-2.5 rounded-sm bg-yellow-300/90" />
                           <span className="absolute bottom-1 right-1 h-2.5 w-2.5 rounded-full bg-orange-400/95" />
                           <span className="absolute bottom-1 right-2.5 h-2.5 w-2.5 rounded-full bg-red-500/90" />
                        </span>
                           <span className={`text-[11px] font-medium leading-tight sm:text-[12px] ${
                             isDark ? 'text-slate-100' : 'text-slate-700'
                           }`}>
                           {t('payment.cardPayment')}
                        </span>
                      </div>
                    ) : choice.kind === 'generic' ? (
                      <div className="flex flex-col items-center gap-1">
                        <WalletCards className={`w-6 h-6 ${isDark ? 'text-gray-300' : 'text-slate-600'}`} />
                        <span className={`text-xs font-medium ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                          {choice.label}
                        </span>
                      </div>
                    ) : choice.gatewayName === 'paypal' ? (
                      // PayPal keeps its logo
                      <img 
                        src={gatewayLogos['paypal']} 
                        alt="PayPal"
                        className="h-10 w-24 object-contain"
                      />
                    ) : choice.gatewayName === 'crypto' ? (
                      // Crypto shows Bitcoin icon + text
                      <div className="flex flex-col items-center gap-1">
                        <Bitcoin className="w-6 h-6 text-orange-500" />
                        <span className={`text-xs font-medium ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>Crypto</span>
                      </div>
                    ) : choice.gatewayName === 'usda_pay' ? (
                      // USDA Pay uses the official USDA mark
                      <div className="flex flex-col items-center gap-1">
                        <img
                          src="/payment-brands/usda-official-logo.png"
                          alt=""
                          aria-hidden="true"
                          className="h-10 w-14 object-contain"
                        />
                         <span className={`text-xs font-medium ${isDark ? 'text-slate-100' : 'text-slate-700'}`}>USDA Pay</span>
                      </div>
                    ) : (
                      // Other gateways show credit card icon + text
                      <div className="flex flex-col items-center gap-1">
                        <CreditCard className={`w-6 h-6 ${isDark ? 'text-gray-400' : 'text-slate-600'}`} />
                         <span className={`text-xs font-medium ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>{t('payment.cardPayment')}</span>
                      </div>
                    )}
                  </Button>
                ))}
              </div>
            </div>
          ) : (
            // SINGLE GATEWAY: Show single button with "Alpha Bet" branding
            <Button
              onClick={() => singleChoice?.gatewayName === 'roxpay'
                ? handlePayment(
                  singleChoice.gatewayName,
                  singleChoice.gatewayId,
                  singleChoice.providerId,
                  singleChoice.methodKey
                )
                : handlePayment()}
              disabled={processing || (!payment_link.amount && !customAmount)}
              className="w-full h-12 text-lg font-medium text-white"
              style={{ backgroundColor: isDark ? '#9D4DFF' : buttonColor }}
              data-testid="pay-button"
            >
              {processing ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                   {t('payment.processing')}
                </>
              ) : (
                <>
                  {singleChoice?.kind === 'wallet' ? (
                    <WalletCards className="mr-2 h-5 w-5" />
                  ) : (
                    <CreditCard className="mr-2 h-5 w-5" />
                  )}
                  {singleChoice?.label || 'Paga con Alpha Bit'}
                </>
              )}
            </Button>
          )}

           {/* Security reassurance */}
             <div className={`mt-5 flex items-center justify-center gap-2 pt-2 text-[14px] ${
               isDark ? 'text-slate-400' : 'text-slate-500'
             }`}>
               <Lock className={`h-5 w-5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`} />
               <span>{t('branding.securePayment')}</span>
           </div>

          {/* Footer */}
          {branding.footer_text && (
               <p className="mt-6 text-center text-xs text-slate-500">
              {branding.footer_text}
            </p>
          )}
        </CardContent>
      </Card>
      </div>
    </div>
  );
}
